import React, { useCallback, useEffect, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import NotesSidebar from '../components/notes/NotesSidebar';
import NoteEditor from '../components/notes/NoteEditor';
import { useNotesStore } from '../store/notes';
import { PageLayout } from '../layout/primitives';

const NotesPage: React.FC = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [notesLoaded, setNotesLoaded] = useState(false);
  const [requestedOpenId, setRequestedOpenId] = useState<string | null>(null);
  const { notes, activeNote, loading, fetchNotes, openNote, clearActiveNote } =
    useNotesStore();
  const openId = searchParams.get('open');

  const setOpenParam = useCallback(
    (id: string | null, replace = false) => {
      setSearchParams(id ? { open: id } : {}, { replace });
    },
    [setSearchParams]
  );

  useEffect(() => {
    let cancelled = false;

    const loadNotes = async () => {
      await fetchNotes();
      if (!cancelled) {
        setNotesLoaded(true);
      }
    };

    void loadNotes();

    return () => {
      cancelled = true;
    };
  }, [fetchNotes]);

  useEffect(() => {
    if (!openId) {
      if (!isDesktop && activeNote) {
        clearActiveNote();
      }
      return;
    }

    if (notesLoaded && !notes.some((note) => note.id === openId)) {
      clearActiveNote();
      setOpenParam(null, true);
      return;
    }

    if (activeNote?.id !== openId && requestedOpenId !== openId) {
      setRequestedOpenId(openId);
      void openNote(openId).finally(() => {
        setRequestedOpenId((current) => (current === openId ? null : current));
      });
    }
  }, [
    activeNote,
    clearActiveNote,
    isDesktop,
    notes,
    notesLoaded,
    openId,
    openNote,
    requestedOpenId,
    setOpenParam,
  ]);

  const handleOpenNote = useCallback(
    (id: string) => {
      setOpenParam(id);
    },
    [setOpenParam]
  );

  const handleCreateNote = useCallback(
    (id: string) => {
      setOpenParam(id);
    },
    [setOpenParam]
  );

  const handleDeleteNote = useCallback(
    (id: string) => {
      if (openId === id) {
        clearActiveNote();
        setOpenParam(null, true);
      }
    },
    [clearActiveNote, openId, setOpenParam]
  );

  const handleBackToList = useCallback(() => {
    clearActiveNote();
    setOpenParam(null, true);
  }, [clearActiveNote, setOpenParam]);

  const showMobileEditor = !isDesktop && Boolean(openId || activeNote);

  return (
    <PageLayout
      fillHeight
      paddingMode="none"
      innerSx={{ overflow: 'hidden' }}
    >
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {isDesktop ? (
          <>
            <NotesSidebar
              selectedNoteId={openId ?? activeNote?.id ?? null}
              onSelectNote={handleOpenNote}
              onCreateNote={handleCreateNote}
              onDeleteNote={handleDeleteNote}
            />
            <NoteEditor loading={loading && Boolean(openId)} />
          </>
        ) : showMobileEditor ? (
          <NoteEditor
            isMobile
            loading={loading && Boolean(openId)}
            onBack={handleBackToList}
          />
        ) : (
          <NotesSidebar
            variant="mobile"
            selectedNoteId={openId ?? activeNote?.id ?? null}
            onSelectNote={handleOpenNote}
            onCreateNote={handleCreateNote}
            onDeleteNote={handleDeleteNote}
          />
        )}
      </Box>
    </PageLayout>
  );
};

export default NotesPage;
